import dayjs, { Dayjs } from "dayjs";
import { CalendarEventResource } from "../models/CalendarEventResource";

class CalendarEventsFilterInfo {
    projectIds: number[] = [];
    archived: boolean = false;
    startAt: Dayjs | null = null;
    endAt: Dayjs | null = null;
    calendarableTypes: string[] = [];
    calendarEventTypes: string[] = [];
    searchText: string = "";
    statuses: string[] = [];
    calendarEventResourcesLogic: "AND" | "OR" = "AND";
    calendarEventResources: CalendarEventResource[] = [];
    includeBackgroundEvents: boolean = true;
    page: number | null = null;
    pageSize: number | null = null;

    constructor() {
        this.projectIds = [];
        this.archived = false;
        this.startAt = dayjs().startOf("month").startOf("week").startOf("day");
        this.endAt = this.startAt.add(6, "week").endOf("week").endOf("day");
        this.calendarableTypes = [];
        this.calendarEventTypes = [];
        this.searchText = "";
        this.statuses = [];
        this.calendarEventResourcesLogic = "OR";
        this.calendarEventResources = [];
        this.includeBackgroundEvents = true;
        this.page = null;
        this.pageSize = null;
    }

    isUnfiltered(): boolean {
        const defaultFilter = new CalendarEventsFilterInfo();

        const defaultDropOffAreaIds = defaultFilter.calendarEventResources
            .filter((res) => res.calendarResourceableType === "Area")
            .map((res) => res.calendarResourceableId);
        const dropOffAreaIds = this.calendarEventResources
            .filter((res) => res.calendarResourceableType === "Area")
            .map((res) => res.calendarResourceableId);

        const defaultEquipmentIds = defaultFilter.calendarEventResources
            .filter((res) => res.calendarResourceableType === "Equipment")
            .map((res) => res.calendarResourceableId);
        const equipmentIds = this.calendarEventResources
            .filter((res) => res.calendarResourceableType === "Equipment")
            .map((res) => res.calendarResourceableId);

        const defaultTeamIds = defaultFilter.calendarEventResources
            .filter((res) => res.calendarResourceableType === "Team")
            .map((res) => res.calendarResourceableId);
        const teamIds = this.calendarEventResources
            .filter((res) => res.calendarResourceableType === "Team")
            .map((res) => res.calendarResourceableId);

        return (
            this.archived === defaultFilter.archived &&
            this.projectIds.length === defaultFilter.projectIds.length &&
            (!this.startAt || this.startAt.isSame(defaultFilter.startAt)) &&
            (!this.endAt || this.endAt.isSame(defaultFilter.endAt)) &&
            dropOffAreaIds.length === defaultDropOffAreaIds.length &&
            equipmentIds.length === defaultEquipmentIds.length &&
            teamIds.length === defaultTeamIds.length &&
            this.statuses.length === defaultFilter.statuses.length &&
            this.searchText === defaultFilter.searchText &&
            this.includeBackgroundEvents === defaultFilter.includeBackgroundEvents
        );
    }

    toApiObject() {
        return {
            project_ids: this.projectIds,
            archived: this.archived,
            start_at: this.startAt?.toString(),
            end_at: this.endAt?.toString(),
            calendarable_types: this.calendarableTypes,
            calendar_event_types: this.calendarEventTypes,
            search_text: this.searchText,
            statuses: this.statuses,
            calendar_event_resources_logic: this.calendarEventResourcesLogic,
            calendar_event_resources: this.calendarEventResources.map((resource) => ({
                calendar_resourceable_type: resource.calendarResourceableType,
                calendar_resourceable_id: resource.calendarResourceableId,
                delivery_role: resource.deliveryRole,
            })),
            include_background_events: this.includeBackgroundEvents,
            page: this.page,
            page_size: this.pageSize,
        };
    }

    filteredTeamIds(): number[] {
        return this.calendarEventResources
            .filter((res) => res.calendarResourceableType === "Team")
            .map((res) => res.calendarResourceableId);
    }

    filteredAreaIds(): number[] {
        return this.calendarEventResources
            .filter((res) => res.calendarResourceableType === "Area")
            .map((res) => res.calendarResourceableId);
    }

    filteredEquipmentIds(): number[] {
        return this.calendarEventResources
            .filter((res) => res.calendarResourceableType === "Equipment")
            .map((res) => res.calendarResourceableId);
    }

    static fromApiObject(obj: any, timezone: string = dayjs.tz.guess()): CalendarEventsFilterInfo {
        const filter = new CalendarEventsFilterInfo();

        filter.projectIds = obj.project_ids || [];
        filter.archived = obj.archived || false;
        filter.startAt = obj.start_at ? dayjs(obj.start_at) : null;
        filter.endAt = obj.end_at ? dayjs(obj.end_at) : null;
        filter.calendarableTypes = obj.calendarable_types || [];
        filter.calendarEventTypes = obj.calendar_event_types || [];
        filter.searchText = obj.search_text || "";
        filter.statuses = obj.statuses || [];
        filter.calendarEventResourcesLogic = obj.calendar_event_resources_logic || "OR";
        filter.calendarEventResources =
            obj.calendar_event_resources?.map((res: any) => CalendarEventResource.fromApiObject(res, timezone)) || [];
        filter.includeBackgroundEvents =
            obj.include_background_events !== undefined ? obj.include_background_events : true;
        filter.page = obj.page || null;
        filter.pageSize = obj.page_size || null;

        return filter;
    }
}

export default CalendarEventsFilterInfo;
